<template>
  <section>
    <div class="mb-2 flex items-center justify-between">
      <h3 class="add-note-title">
        Add New Internal Note
      </h3>
    </div>
    <div class="add-note-meta-row mb-2">
      <p class="new-internal-note-label">
        New Internal Note
      </p>
      <p class="staff-only-label">
        (Staff Only)
      </p>
    </div>

    <div class="note-textarea-wrapper">
      <MentionTextarea
        ref="mentionTextareaRef"
        v-model="localValue"
        :customer-name-map="customerNameMap"
        :customer-list="customerList"
      />
      <!-- Character counter removed - no limit -->
    </div>

    <div class="add-note-divider"></div>

    <div class="flex justify-between items-center mt-4">
      <NewLibraryButton
        type="normal"
        color-type="red-stroke"
        @click="$emit('cancel')"
      >
        Cancel
      </NewLibraryButton>

      <NewLibraryButton
        type="normal"
        color-type="dark-blue"
        :disabled="!localValue.trim()"
        @click="$emit('submit')"
      >
        Finish and add
      </NewLibraryButton>
    </div>
  </section>
</template>

<script>
import NewLibraryButton from '@/NewLibrary/Button.vue';
import MentionTextarea from './MentionTextarea.vue';

export default {
  name: 'AddInternalNoteForm',
  components: {
    NewLibraryButton,
    MentionTextarea,
  },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    customerNameMap: {
      type: Object,
      default: () => ({})
    },
    customerList: {
      type: Array,
      default: () => []
    },
  },
  emits: ['update:modelValue', 'cancel', 'submit'],
  computed: {
    localValue: {
      get() {
        return this.modelValue;
      },
      set(v) {
        this.$emit('update:modelValue', v);
      },
    },

    // Get display text length for character counter
    displayTextLength() {
      return this.getDisplayTextLength(this.localValue);
    },
  },
  methods: {
    // Get display text length (replace backend format with provider names)
    getDisplayTextLength(backendText) {
      if (!backendText) return 0;

      // Replace @doctor_CUSTOMER_ID_CLINIC_ID with @ProviderName and count
      const mentionRegex = /@doctor_(\w+)_\w+/g;
      const displayText = backendText.replace(mentionRegex, (match, customerId) => {
        const providerName = this.customerNameMap[customerId];
        return providerName ? `@${providerName}` : match;
      });

      return displayText.length;
    },

    // Get transformed content for backend submission
    getTransformedContent() {
      if (!this.$refs.mentionTextareaRef) {
        return this.localValue;
      }

      const transformed = this.$refs.mentionTextareaRef.transformContent(this.localValue);
      return transformed;
    },
  },
};
</script>

<style scoped>
.add-note-title {
  color: var(--TrueBlack, #333333);
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.15px;
}

.new-internal-note-label {
  color: var(--TrueBlack, #333333);
  font-family: Lato;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}

.staff-only-label {
  color: var(--ErrorRed, #D10000);
  font-family: Lato;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}

.character-counter {
  color: var(--ContentMediumGrey, #999999);
  font-family: "IBM Plex Sans Hebrew", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.15px;
}

/* MentionTextarea styles */
.note-textarea-wrapper :deep(.mention-editor) {
  color: var(--TrueBlack, #333333);
  font-family: "IBM Plex Sans Hebrew", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.15px;
  border: 1px solid #D7E3EC !important;
  box-shadow: none;
  min-height: 328px !important;
  max-height: 328px !important;
  padding: 12px !important;
  padding-bottom: 28px !important;
  background: white;
  border-radius: 4px;
}

.note-textarea-wrapper :deep(.mention-editor:focus) {
  border-color: var(--OldVibrantGreen, #25B8C2) !important;
  outline: none;
}

.note-textarea-wrapper :deep(.mention-editor::placeholder) {
  color: var(--ContentMediumGrey, #999999);
}

/* Legacy el-input styles (kept for compatibility) */
.note-textarea-wrapper :deep(.el-textarea__inner) {
  color: var(--TrueBlack, #333333);
  font-family: "IBM Plex Sans Hebrew", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.15px;
  border: 1px solid #D7E3EC !important;
  box-shadow: none;
  height: 328px;
  padding: 12px !important;
}

.note-textarea-wrapper :deep(.el-textarea__inner::placeholder) {
  color: var(--ContentMediumGrey, #999999);
}

.note-textarea-wrapper {
  position: relative;
}

.character-counter-inside {
  position: absolute;
  bottom: 8px;
  left: 16px;
  color: var(--ContentMediumGrey, #999999);
  font-family: "IBM Plex Sans Hebrew", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.15px;
  pointer-events: none;
  z-index: 1;
}

.character-counter-row {
  margin-top: 8px;
  color: var(--ContentMediumGrey, #999999);
  font-family: "IBM Plex Sans Hebrew", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.15px;
}

.add-note-meta-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 4px;
  flex-shrink: 0;
}

.add-note-divider {
  border-top: 1px dashed var(--DividerGray, #C4C4C4);
  margin: 24px 0 20px 0;
}
</style>
